/**
 * SelectionDocsUploadModal Component
 *
 * Reusable modal for bulk uploading selection documents.
 * Can be used across Admin Dashboard, Admin Projects, Admin Uploads, etc.
 * Supports optional project pre-selection (locked when projectId is provided).
 *
 * @component
 * @version 1.0.0
 * @created 2025-12-30
 *
 * Features:
 * - Project selector (can be pre-filled and locked)
 * - Multiple category selection with file uploads per category
 * - Drag and drop support for each category
 * - Upload to Cloudinary then link to Airtable
 * - Progress tracking during upload
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  FaTimes,
  FaCloudUploadAlt,
  FaFileAlt,
  FaPlus,
  FaUpload,
  FaSpinner,
  FaExclamationTriangle,
  FaHome,
} from "react-icons/fa";
import { useActiveProjects } from "../../hooks/useProjects";
import { useUploadSelectionDocuments } from "../../hooks/useSelections";
import useCurrentTeamMember from "../../hooks/useCurrentTeamMember";
import "./SelectionDocsUploadModal.css";

/**
 * Selection document categories matching Airtable schema.
 *
 * - `key` is the Airtable-side category identifier (camelCase) used by
 *   the `uploadSelectionDocument` backend function.
 * - `slug` is the normalized snake_case slug used for the Cloudinary
 *   folder structure and `category` context metadata so slugs match the
 *   convention used by the other uploaders (e.g. "floorplans").
 * - `fieldName` is the Airtable attachment field the upload writes to.
 */
const SELECTION_CATEGORIES = [
  {
    key: "roofGutter",
    slug: "roof_gutter",
    label: "Gutters",
    fieldName: "Roof/Gutter Documents",
  },
  {
    key: "doorsWindows",
    slug: "doors_windows",
    label: "Doors & Windows",
    fieldName: "Doors/Windows Documents",
  },
  {
    key: "garageDoor",
    slug: "garage_door",
    label: "Garage Door",
    fieldName: "Garage Door Documents",
  },
  {
    key: "fireplace",
    slug: "fireplace",
    label: "Fireplace",
    fieldName: "Fireplace Documents",
  },
  {
    key: "cabinets",
    slug: "cabinets",
    label: "Cabinets",
    fieldName: "Cabinets Documents",
  },
  {
    key: "countertopsSinks",
    slug: "countertops_sinks",
    label: "Countertops & Sinks",
    fieldName: "Countertops/Sinks Documents",
  },
  {
    key: "showersTubs",
    slug: "showers_tubs",
    label: "Shower Glass",
    fieldName: "Showers/Tubs Documents",
  },
  {
    key: "plumbingFixtures",
    slug: "plumbing_fixtures",
    label: "Plumbing Fixtures",
    fieldName: "Plumbing Fixture Documents",
  },
  {
    key: "lightingFixtures",
    slug: "lighting_fixtures",
    label: "Lighting Fixtures",
    fieldName: "Light Fixture Documents",
  },
  {
    key: "flooring",
    slug: "flooring",
    label: "Flooring",
    fieldName: "Flooring Documents",
  },
  {
    key: "tile",
    slug: "tile",
    label: "Tile",
    fieldName: "Tile Documents",
  },
  {
    key: "finalSelectionsPDF",
    slug: "final_selections_pdf",
    label: "Final Selections PDF",
    fieldName: "Final Selections Document",
  },
];

/**
 * SelectionDocsUploadModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback when modal closes
 * @param {string|null} props.projectId - Pre-selected project ID (locks selection if provided)
 * @param {string|null} props.projectName - Pre-selected project name for display
 * @param {Function} props.onUploadComplete - Callback after successful upload
 * @returns {JSX.Element|null} SelectionDocsUploadModal component
 */
function SelectionDocsUploadModal({
  isOpen,
  onClose,
  projectId = null,
  projectName = null,
  onUploadComplete,
  showTestAccounts = false,
}) {
  // ============ STATE ============
  // Each entry: { id, category, files }
  const [uploadEntries, setUploadEntries] = useState([
    { id: Date.now(), category: SELECTION_CATEGORIES[0]?.key || "", files: [] },
  ]);
  const [activeDragId, setActiveDragId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || "");
  const [selectedProjectName, setSelectedProjectName] = useState(
    projectName || "",
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    currentFile: "",
  });

  // ============ HOOKS ============
  const { data: projects, isLoading: projectsLoading } = useActiveProjects({
    showTestAccounts,
  });
  const uploadMutation = useUploadSelectionDocuments();
  // Canonical RSH team member name for Cloudinary `uploaded_by_name`
  const { name: teamMemberName } = useCurrentTeamMember();

  // ============ EFFECTS ============
  /**
   * Sync project selection from props in both directions: apply the
   * parent's value when supplied, clear when the parent removes it
   * (e.g., AdminUploads' top selector switched back to "All projects").
   */
  useEffect(() => {
    setSelectedProjectId(projectId || "");
    setSelectedProjectName(projectName || "");
  }, [projectId, projectName]);

  /**
   * Reset state when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      setUploadEntries([
        {
          id: Date.now(),
          category: SELECTION_CATEGORIES[0]?.key || "",
          files: [],
        },
      ]);
      setUploadError(null);
      setUploadProgress({ current: 0, total: 0, currentFile: "" });
      setSelectedProjectId(projectId || "");
      setSelectedProjectName(projectName || "");
    }
  }, [isOpen, projectId, projectName]);

  // ============ HANDLERS ============

  /**
   * Add a new upload entry
   */
  const addEntry = useCallback(() => {
    // Find first category not already selected
    const usedCategories = uploadEntries.map((e) => e.category);
    const availableCategory =
      SELECTION_CATEGORIES.find((c) => !usedCategories.includes(c.key))?.key ||
      SELECTION_CATEGORIES[0]?.key;

    setUploadEntries((prev) => [
      ...prev,
      { id: Date.now(), category: availableCategory, files: [] },
    ]);
  }, [uploadEntries]);

  /**
   * Remove an upload entry
   * @param {number} entryId - The entry ID to remove
   */
  const removeEntry = useCallback(
    (entryId) => {
      if (uploadEntries.length > 1) {
        setUploadEntries((prev) => prev.filter((e) => e.id !== entryId));
      }
    },
    [uploadEntries.length],
  );

  /**
   * Update category for an entry
   * @param {number} entryId - The entry ID to update
   * @param {string} category - The new category key
   */
  const updateEntryCategory = useCallback((entryId, category) => {
    setUploadEntries((prev) =>
      prev.map((e) => (e.id === entryId ? { ...e, category } : e)),
    );
  }, []);

  /**
   * Add files to an entry
   * @param {number} entryId - The entry ID to add files to
   * @param {File[]} newFiles - Array of files to add
   */
  const addFilesToEntry = useCallback((entryId, newFiles) => {
    setUploadEntries((prev) =>
      prev.map((e) =>
        e.id === entryId ? { ...e, files: [...e.files, ...newFiles] } : e,
      ),
    );
  }, []);

  /**
   * Remove a file from an entry
   * @param {number} entryId - The entry ID to remove file from
   * @param {number} fileIndex - The index of the file to remove
   */
  const removeFileFromEntry = useCallback((entryId, fileIndex) => {
    setUploadEntries((prev) =>
      prev.map((e) =>
        e.id === entryId
          ? { ...e, files: e.files.filter((_, i) => i !== fileIndex) }
          : e,
      ),
    );
  }, []);

  /**
   * Handle file input change
   * @param {number} entryId - The entry ID to add files to
   * @param {Event} e - The file input change event
   */
  const handleFileSelect = useCallback(
    (entryId, e) => {
      const files = Array.from(e.target.files);
      addFilesToEntry(entryId, files);
      e.target.value = ""; // Reset input
    },
    [addFilesToEntry],
  );

  /**
   * Handle drag over event
   * @param {number} entryId - The entry ID being dragged over
   * @param {DragEvent} e - The drag event
   */
  const handleDragOver = useCallback((entryId, e) => {
    e.preventDefault();
    setActiveDragId(entryId);
  }, []);

  /**
   * Handle drag leave event
   * @param {DragEvent} e - The drag event
   */
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setActiveDragId(null);
  }, []);

  /**
   * Handle file drop event
   * @param {number} entryId - The entry ID to add files to
   * @param {DragEvent} e - The drop event
   */
  const handleDrop = useCallback(
    (entryId, e) => {
      e.preventDefault();
      setActiveDragId(null);
      const files = Array.from(e.dataTransfer.files);
      addFilesToEntry(entryId, files);
    },
    [addFilesToEntry],
  );

  /**
   * Handle final upload
   */
  const handleUpload = async () => {
    // Validate project selection
    if (!selectedProjectId) {
      setUploadError("Please select a project");
      return;
    }

    // Filter entries that have files
    const validEntries = uploadEntries.filter((e) => e.files.length > 0);

    if (validEntries.length === 0) {
      setUploadError("Please add at least one file to upload");
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    // Calculate total files for progress
    const totalFiles = validEntries.reduce((sum, e) => sum + e.files.length, 0);
    setUploadProgress({ current: 0, total: totalFiles, currentFile: "" });

    try {
      // Upload each entry's files to the appropriate category
      let filesUploaded = 0;

      for (const entry of validEntries) {
        const categoryInfo = SELECTION_CATEGORIES.find(
          (c) => c.key === entry.category,
        );

        for (const file of entry.files) {
          setUploadProgress({
            current: filesUploaded,
            total: totalFiles,
            currentFile: file.name,
          });

          // Resolve the project name with a defensive cascade so both
          // locked (prop-supplied) and unlocked (dropdown-captured) flows
          // always populate Cloudinary's project_name context.
          const matchedProject = projects?.find(
            (p) => p.id === selectedProjectId,
          );
          const resolvedProjectName =
            selectedProjectName ||
            projectName ||
            matchedProject?.projectName ||
            matchedProject?.name ||
            "";

          await uploadMutation.mutateAsync({
            projectId: selectedProjectId,
            projectName: resolvedProjectName,
            category: entry.category,
            categorySlug: categoryInfo?.slug || entry.category,
            categoryLabel: categoryInfo?.label || entry.category,
            fieldName: categoryInfo?.fieldName,
            uploadedByName: teamMemberName,
            file: file,
          });

          filesUploaded++;
          setUploadProgress({
            current: filesUploaded,
            total: totalFiles,
            currentFile: file.name,
          });
        }
      }

      // Success - call callback and close
      if (onUploadComplete) {
        onUploadComplete(validEntries);
      }
      onClose();
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError(error.message || "Failed to upload files");
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!isUploading) {
      onClose();
    }
  };

  /**
   * Handle overlay click (close modal)
   * @param {Event} e - The click event
   */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isUploading) {
      onClose();
    }
  };

  // ============ COMPUTED VALUES ============
  // Count entries with files
  const entriesWithFiles = uploadEntries.filter(
    (e) => e.files.length > 0,
  ).length;
  const totalFiles = uploadEntries.reduce((sum, e) => sum + e.files.length, 0);

  // Get categories already used (for showing which are taken)
  const usedCategories = uploadEntries.map((e) => e.category);

  // Check if project is locked (pre-selected)
  const isProjectLocked = Boolean(projectId);

  // Get display name for locked project
  // Use provided projectName, or look up from projects list, or show loading
  const lockedProjectDisplayName =
    projectName ||
    projects?.find((p) => p.id === projectId)?.name ||
    "Loading project...";

  // ============ RENDER ============
  if (!isOpen) return null;

  return (
    <div className="sdm-modal-overlay" onClick={handleOverlayClick}>
      <div className="sdm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sdm-modal-header">
          <div className="sdm-modal-header-left">
            <div className="sdm-modal-header-icon">
              <FaUpload />
            </div>
            <h3>Upload Selection Documents</h3>
          </div>
          <button
            className="sdm-modal-close"
            onClick={handleClose}
            disabled={isUploading}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="sdm-modal-body">
          {/* Project Selector */}
          <div className="sdm-project-selector">
            <label className="sdm-label">
              <FaHome /> Project
            </label>
            {isProjectLocked ? (
              <input
                type="text"
                value={lockedProjectDisplayName}
                disabled
                className="sdm-input sdm-input-locked"
              />
            ) : (
              <select
                value={selectedProjectId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedProjectId(nextId);
                  const match = projects?.find((p) => p.id === nextId);
                  setSelectedProjectName(match?.name || "");
                }}
                className="sdm-select"
                disabled={projectsLoading || isUploading}
              >
                <option value="">Select a project...</option>
                {projects?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Instructions */}
          <p className="sdm-instructions">
            Select a document category, add files, then add another category if
            needed.
          </p>

          {/* Upload Entries */}
          <div className="sdm-entries">
            {uploadEntries.map((entry, index) => (
              <div key={entry.id} className="sdm-entry">
                <div className="sdm-entry-header">
                  <span className="sdm-entry-number">{index + 1}</span>
                  <select
                    value={entry.category}
                    onChange={(e) =>
                      updateEntryCategory(entry.id, e.target.value)
                    }
                    className="sdm-entry-select"
                    disabled={isUploading}
                  >
                    {SELECTION_CATEGORIES.map((cat) => (
                      <option
                        key={cat.key}
                        value={cat.key}
                        disabled={
                          usedCategories.includes(cat.key) &&
                          entry.category !== cat.key
                        }
                      >
                        {cat.label}{" "}
                        {usedCategories.includes(cat.key) &&
                        entry.category !== cat.key
                          ? "(selected)"
                          : ""}
                      </option>
                    ))}
                  </select>
                  {uploadEntries.length > 1 && !isUploading && (
                    <button
                      className="sdm-entry-remove"
                      onClick={() => removeEntry(entry.id)}
                      data-tooltip="Remove this category"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                {/* Drop Zone */}
                <div
                  className={`sdm-dropzone ${
                    activeDragId === entry.id ? "dragging" : ""
                  } ${isUploading ? "disabled" : ""}`}
                  onDragOver={(e) => handleDragOver(entry.id, e)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(entry.id, e)}
                >
                  <input
                    type="file"
                    id={`sdm-upload-${entry.id}`}
                    className="sdm-file-input"
                    multiple
                    onChange={(e) => handleFileSelect(entry.id, e)}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    disabled={isUploading}
                  />
                  <label
                    htmlFor={`sdm-upload-${entry.id}`}
                    className="sdm-dropzone-content"
                  >
                    <FaCloudUploadAlt className="sdm-dropzone-icon" />
                    <p className="sdm-dropzone-text">
                      Drag & drop files here or click to browse
                    </p>
                    <p className="sdm-dropzone-hint">
                      PDF, JPG, PNG, DOC, XLS - Max 10MB per file
                    </p>
                  </label>
                </div>

                {/* Files List */}
                {entry.files.length > 0 && (
                  <div className="sdm-files">
                    {entry.files.map((file, fileIndex) => (
                      <div key={fileIndex} className="sdm-file">
                        <FaFileAlt />
                        <span className="sdm-file-name">{file.name}</span>
                        <span className="sdm-file-size">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                        {!isUploading && (
                          <button
                            className="sdm-file-remove"
                            onClick={() =>
                              removeFileFromEntry(entry.id, fileIndex)
                            }
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add Another Category Button */}
          {uploadEntries.length < SELECTION_CATEGORIES.length &&
            !isUploading && (
              <button className="sdm-add-entry" onClick={addEntry}>
                <FaPlus /> Add Another Category
              </button>
            )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="sdm-progress">
              <div className="sdm-progress-bar">
                <div
                  className="sdm-progress-fill"
                  style={{
                    width: `${
                      uploadProgress.total > 0
                        ? (uploadProgress.current / uploadProgress.total) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
              <p className="sdm-progress-text">
                Uploading {uploadProgress.current} of {uploadProgress.total}...
                {uploadProgress.currentFile && (
                  <span className="sdm-progress-file">
                    {uploadProgress.currentFile}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Error Message */}
          {uploadError && (
            <div className="sdm-error">
              <FaExclamationTriangle /> {uploadError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sdm-modal-footer">
          <button
            className="sdm-cancel-btn"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            className="sdm-upload-btn"
            onClick={handleUpload}
            disabled={totalFiles === 0 || !selectedProjectId || isUploading}
          >
            {isUploading ? (
              <>
                <FaSpinner className="sdm-spinner" /> Uploading...
              </>
            ) : (
              <>
                <FaUpload /> Upload {totalFiles} File
                {totalFiles !== 1 ? "s" : ""} to {entriesWithFiles} Categor
                {entriesWithFiles !== 1 ? "ies" : "y"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SelectionDocsUploadModal;
